/**
 * KimiEditPanel - Kimi-style right sidebar editor
 * 
 * Exact Kimi UI parity:
 * - "EDIT" header
 * - Section title with ID (e.g., "Hero sec-1")
 * - Accordion sections: Layout, Background, Content, Cards, Look, Spacing, Advanced
 * - Collapsible with arrow indicators
 * - Subtle icons and descriptions
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronDown, ChevronRight, Layout, Palette, Type, 
  CreditCard, Eye, Maximize2, Settings, Layers, Link2, ExternalLink, Info
} from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { 
  LayoutInspector, 
  DEFAULT_LAYOUT, 
  DEFAULT_BACKGROUND 
} from '../../admin/page_builder';

// Accordion section component - Kimi style
const EditSection = ({ 
  icon: Icon, 
  title, 
  subtitle,
  defaultOpen = false, 
  children 
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-zinc-800/30">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-900/30 transition-colors"
      >
        <div className="text-zinc-500">
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 text-left">
          <div className="text-[13px] font-medium text-white">{title}</div>
          {subtitle && (
            <div className="text-[11px] text-zinc-500">{subtitle}</div>
          )}
        </div>
        <div className="text-zinc-600">
          {isOpen ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </div>
      </button>
      
      {isOpen && (
        <div className="px-4 pb-4 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

// Format section ID like "sec-1"
const formatSectionId = (id, index) => {
  return `sec-${index + 1}`;
};

const KimiEditPanel = ({
  section,
  sectionIndex = 0,
  schema,
  onUpdateProps,
  onUpdateLayout,
  onUpdateBackground,
}) => {
  const navigate = useNavigate();
  const layout = section?.layout || DEFAULT_LAYOUT;
  const background = section?.background || DEFAULT_BACKGROUND;
  const settingsKey = section?.settings_key;
  
  // Determine section display name
  const sectionName = schema?.label || section?.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Section';
  const sectionId = formatSectionId(section?.id, sectionIndex);
  
  // Get current layout/background labels
  const layoutLabel = layout.preset?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Single';
  const backgroundLabel = background?.type || 'none';
  
  // Map settings_key to Site Manager tab
  const SETTINGS_KEY_TO_TAB = {
    'homepage.hero': 'hero',
    'homepage.banner': 'banner',
    'homepage.announcement': 'announcement',
    'homepage.featured': 'featured',
  };
  const siteManagerTab = settingsKey ? SETTINGS_KEY_TO_TAB[settingsKey] || 'landing' : null;
  
  return (
    <aside 
      className="w-[260px] bg-zinc-950 border-l border-zinc-800/30 flex flex-col h-full"
      data-testid="kimi-edit-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800/30">
        <h2 className="text-[11px] font-medium text-zinc-400 tracking-wide">
          EDIT
        </h2>
      </div>
      
      {/* Section identifier */}
      {section && (
        <div className="px-4 py-3 border-b border-zinc-800/30 bg-zinc-900/30">
          <div className="flex items-center gap-3">
            <div className={`p-1.5 rounded ${settingsKey ? 'bg-blue-500/20' : 'bg-orange-500/20'}`}>
              {settingsKey ? (
                <Link2 className="w-4 h-4 text-blue-400" />
              ) : (
                <Layers className="w-4 h-4 text-orange-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-white">{sectionName}</div>
              <div className="text-[11px] text-zinc-500">{sectionId}</div>
            </div>
          </div>
          {settingsKey && (
            <div className="mt-2 flex items-center gap-1.5 px-2 py-1 rounded bg-blue-500/10 border border-blue-500/20" data-testid="settings-key-indicator">
              <Link2 className="w-3 h-3 text-blue-400 shrink-0" />
              <span className="text-[10px] text-blue-300 font-medium">Linked to Site Manager</span>
              <span className="text-[9px] text-blue-400/60 font-mono ml-auto">{settingsKey}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Edit sections */}
      <div className="flex-1 overflow-y-auto">
        {section ? (
          <>
            {/* Layout */}
            <EditSection 
              icon={Layout} 
              title="Layout" 
              subtitle={layoutLabel}
              defaultOpen={true}
            >
              <LayoutInspector
                layout={layout}
                background={background}
                onLayoutChange={onUpdateLayout}
                onBackgroundChange={onUpdateBackground}
                templateId={section?._templateId || section?.type}
              />
            </EditSection>
            
            {/* Background */}
            <EditSection 
              icon={Palette} 
              title="Background" 
              subtitle={backgroundLabel}
            >
              <p className="text-[11px] text-zinc-500">
                Background controls are integrated in Layout above.
              </p>
            </EditSection>
            
            {/* Content — hidden for Site Manager-linked blocks */}
            {settingsKey ? (
              <div className="border-b border-zinc-800/30">
                {/* "Edit in Site Manager" CTA */}
                <div className="px-4 py-4 space-y-3" data-testid="site-manager-cta">
                  <div className="flex items-start gap-2">
                    <Info className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                      Content for this block is managed in <span className="text-blue-300 font-medium">Site Manager</span> and injected at render time.
                      You can change its position and layout here.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/admin/site-manager`)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg
                               bg-blue-600/20 border border-blue-500/30 text-blue-300 
                               hover:bg-blue-600/30 hover:border-blue-500/50 hover:text-blue-200
                               transition-all text-[12px] font-medium"
                    data-testid="edit-in-site-manager-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Edit in Site Manager
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Content */}
                <EditSection 
                  icon={Type} 
                  title="Content" 
                  subtitle="Words, images, media"
                  defaultOpen={schema?.editableFields?.length > 0}
                >
                  {schema?.editableFields?.length > 0 ? (
                    schema.editableFields.map(field => (
                      <div key={field.key} className="space-y-1.5">
                        <Label className="text-zinc-400 text-[11px]">{field.label}</Label>
                        {field.type === 'text' && (
                          <Input
                            value={section.props?.[field.key] ?? schema.defaultProps?.[field.key] ?? ''}
                            onChange={(e) => onUpdateProps(field.key, e.target.value)}
                            className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
                          />
                        )}
                        {field.type === 'textarea' && (
                          <Textarea
                            value={section.props?.[field.key] ?? schema.defaultProps?.[field.key] ?? ''}
                            onChange={(e) => onUpdateProps(field.key, e.target.value)}
                            className="bg-zinc-900 border-zinc-800 text-white min-h-[50px] text-[12px]"
                          />
                        )}
                        {field.type === 'number' && (
                          <Input
                            type="number"
                            value={section.props?.[field.key] ?? schema.defaultProps?.[field.key] ?? 0}
                            onChange={(e) => onUpdateProps(field.key, parseInt(e.target.value))}
                            className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
                          />
                        )}
                        {field.type === 'boolean' && (
                          <Switch
                            checked={section.props?.[field.key] ?? schema.defaultProps?.[field.key] ?? false}
                            onCheckedChange={(checked) => onUpdateProps(field.key, checked)}
                          />
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-[11px] text-zinc-500">
                      No editable content fields for this section.
                    </p>
                  )}
                </EditSection>
                
                {/* Cards */}
                <EditSection 
                  icon={CreditCard} 
                  title="Cards" 
                  subtitle="Reading, Discovery, Featured"
                >
                  <p className="text-[11px] text-zinc-500">
                    Card variant selection coming soon.
                  </p>
                </EditSection>
                
                {/* Look */}
                <EditSection 
                  icon={Eye} 
                  title="Look" 
                  subtitle="Colors, type, feel"
                >
                  <p className="text-[11px] text-zinc-500">
                    Visual style controls coming soon.
                  </p>
                </EditSection>
              </>
            )}
            
            {/* Spacing */}
            <EditSection 
              icon={Maximize2} 
              title="Spacing" 
              subtitle="Breathing room"
            >
              <p className="text-[11px] text-zinc-500">
                Margin and padding controls coming soon.
              </p>
            </EditSection>
            
            {/* Advanced */}
            <EditSection 
              icon={Settings} 
              title="Advanced" 
              subtitle="For power users"
            >
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-[11px]">Section ID</Label>
                <Input
                  value={section.id}
                  disabled
                  className="bg-zinc-900/50 border-zinc-800 text-zinc-500 h-8 text-[11px] font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-[11px]">Block Type</Label>
                <Input
                  value={section.type}
                  disabled
                  className="bg-zinc-900/50 border-zinc-800 text-zinc-500 h-8 text-[11px] font-mono"
                />
              </div>
            </EditSection>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-zinc-600 text-[13px] px-4 text-center">
            Select a section on the canvas to edit
          </div>
        )}
      </div>
      
      {/* Page Settings link at bottom */}
      <div className="p-3 border-t border-zinc-800/30">
        <button
          className="w-full flex items-center justify-center gap-2 px-3 py-2 
                     text-[12px] text-zinc-500 hover:text-zinc-300
                     rounded transition-colors"
        >
          <Settings className="w-3.5 h-3.5" />
          <span>Page Settings</span>
        </button>
      </div>
    </aside>
  );
};

export default KimiEditPanel;
